import React, { useState, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import { Table, ButtonGroup, Button, Spinner } from "react-bootstrap";
import ProductCard from "../../components/productCard/productCard";
import "./comparePage.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Product } from "../../models/product";
import apiService from "../../services/ApiService";
import Constants from "../../helpers/Constants";
import ImageSlide from "./imageSlide";
import styled from "styled-components";

const DivComponent = styled.div<{ width: string }>`
  width: ${(props) => props.width};
`;

const SlideSettings = [
  {
    breakpoint: 768,
    products: 2,
    slidesToShow: 4,
  },
  {
    breakpoint: 992,
    products: 3,
    slidesToShow: 5,
  },
  {
    breakpoint: 1024,
    products: 3,
    slidesToShow: 5,
  },
  {
    breakpoint: 1400,
    products: 3,
    slidesToShow: 5,
  },
  {
    breakpoint: 1514,
    products: 4,
    slidesToShow: 5,
  },
  {
    breakpoint: 102400,
    products: Constants.MaxProductsCompared,
    slidesToShow: 5,
  },
];

const getSlideSetting = () => {
  const windowWidth = window.innerWidth;
  return SlideSettings.find((each) => windowWidth < each.breakpoint);
};

export const ComparePage = () => {
  const location = useLocation();
  const ids = location.state as string[];

  const percentage = (ids.length / Constants.MaxProductsCompared) * 100;

  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [windowWidth, setWindowWidth] = useState(0);
  useEffect(() => {
    setIsLoading(true);
    apiService
      .get(`${Constants.ApiPath.ProductCompare}`, { ids })
      .then((result) => {
        if (result && result.data && result.data.length > 1) {
          setProducts(result.data);

          const productlist = getSelectedProducts(result.data);
          setSelectedProducts(productlist);

          const attributes = getAllAttributes(productlist);
          setAttributes(attributes);
        }
        setIsLoading(false);
      });
  }, [ids, windowWidth]);

  const handleResize = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { products: maxCompareNumber, slidesToShow } = getSlideSetting()!;
  const getSelectedProducts = (products: Product[]) => {
    const productlist = [];
    for (
      let index = 0;
      index < products.length && index < maxCompareNumber;
      index++
    ) {
      const product = products[index];
      productlist.push(product);
    }
    return productlist;
  };

  const getAllAttributes = (products: Product[]) => {
    const attributes: string[] = [];
    Object.keys(products[0].attributes).forEach((attribute) => {
      const value = products[0].attributes[attribute];
      if (value === "N/A") {
        let result = true;
        for (let index = 1; index < products.length; index++) {
          if (value !== products[index].attributes[attribute]) {
            result = false;
            break;
          }
        }
        if (!result) attributes.push(attribute);
      } else {
        attributes.push(attribute);
      }
    });
    return attributes;
  };

  const getDiffAttributes = (products: Product[]) => {
    const attributes: string[] = [];
    Object.keys(products[0].attributes).forEach((attribute) => {
      const value = products[0].attributes[attribute];
      for (let index = 1; index < products.length; index++) {
        if (value !== products[index].attributes[attribute]) {
          attributes.push(attribute);
          break;
        }
      }
    });
    return attributes;
  };

  const getSameAttributes = (products: Product[]) => {
    const attributes: string[] = [];
    Object.keys(selectedProducts[0].attributes).forEach((attribute) => {
      const value = selectedProducts[0].attributes[attribute];
      let result = true;
      for (let index = 1; index < selectedProducts.length; index++) {
        if (value !== selectedProducts[index].attributes[attribute]) {
          result = false;
          break;
        }
      }
      if (result && value !== "N/A") attributes.push(attribute);
    });
    return attributes;
  };

  const handleSelectionChange = (sku: string, selected: boolean) => {
    if (!selected) {
      setSelectedProducts((prevValue) => {
        return prevValue.filter((p) => p.sku !== sku);
      });
    } else {
      const product = products.find((p) => p.sku === sku);
      if (product) {
        setSelectedProducts((prevValue) => {
          return [...prevValue, product];
        });
      }
    }
  };

  const [currentView, setCurrentView] = useState("all");
  const [attributes, setAttributes] = useState<string[]>([]);
  useEffect(() => {
    if (!selectedProducts || selectedProducts.length <= 0) return;
    if (selectedProducts.length === 1) {
      handleShowAllAttributes();
    } else {
      if (currentView === "all") {
        setAttributes(getAllAttributes(selectedProducts));
      } else if (currentView === "diff") {
        setAttributes(getDiffAttributes(selectedProducts));
      } else {
        setAttributes(getSameAttributes(selectedProducts));
      }
    }
  }, [selectedProducts]);
  const handleShowAllAttributes = () => {
    setCurrentView("all");
    setAttributes(getAllAttributes(selectedProducts));
  };
  const handleShowDiffAttributes = () => {
    setCurrentView("diff");
    setAttributes(getDiffAttributes(selectedProducts));
  };
  const handleShowSameAttributes = () => {
    setCurrentView("same");
    setAttributes(getSameAttributes(selectedProducts));
  };

  const navigate = useNavigate();

  const handleCloseComparison = () => {
    navigate(-1);
  }
  return isLoading ? (
    <Container className="category-page-loading">
      <Spinner animation="border" />
      <p>Loading...</p>
    </Container>
  ) : (
    <Container className="landing-page-body">
      <Container
        className={`compare-page-content-container ${
          window.innerHeight > 600 ? "compare-page-sticky-header" : ""
        }`}
      >
        <div className="compare-page-product-card">
          {selectedProducts.map((product) => {
            const width = 100 / selectedProducts.length;
            return window.innerWidth < 1200 ? (
              <ProductCard
                model={product}
                key={product.sku}
                width={`${width}%`}
              />
            ) : (
              <ProductCard model={product} key={product.sku} />
            );
          })}
        </div>     
      </Container> 
      {products.length > 2 && (
        <Container>
          <div className="compare-page-product-slideshow">
            <span> QUANTITY SELECTED: {selectedProducts.length} </span>
            <ImageSlide
              slidesToShow={slidesToShow}
              products={products}
              selectedProducts={selectedProducts}
              maxCompareNumber={maxCompareNumber}
              onSelectChange={handleSelectionChange}
            />
          </div>
        </Container>
      )}     
      <Container className="compare-page-buttons-container">
        <DivComponent className="compare-page-buttons" width={`${percentage}%`}>
          <ButtonGroup>
            <Button
              className={
                currentView === "all"
                  ? "compare-page-btn-active"
                  : "compare-page-btn"
              }
              onClick={handleShowAllAttributes}
            >
              ALL DETAILS
            </Button>
            <Button
              className={
                currentView === "diff"
                  ? "compare-page-btn-active"
                  : "compare-page-btn"
              }
              onClick={handleShowDiffAttributes}
            >
              DIFFERENCES
            </Button>
            <Button
              className={
                currentView === "same"
                  ? "compare-page-btn-active"
                  : "compare-page-btn"
              }
              onClick={handleShowSameAttributes}
            >
              SIMILARITIES
            </Button>
          </ButtonGroup>
          <Button
              className="goback-page-button"
              onClick={handleCloseComparison}
              >
              CLOSE COMPARISON
            </Button>
        </DivComponent>
      </Container>
      <Container className="compare-page-attributes">
        <div className="table-responsive">
          <Table striped bordered>
            <tbody>
              {attributes.map((attribute, i) => {
                let isDiff = false;
                const value = selectedProducts[0].attributes[attribute];
                for (let index = 1; index < selectedProducts.length; index++) {
                  if (value !== selectedProducts[index].attributes[attribute]) {
                    isDiff = true;
                    break;
                  }
                }
                return (
                  <React.Fragment key={`attribute-${attribute}-${i}`}>
                    <tr key={`attribute-${attribute}-${i}-header`}>
                      <td className="compare-page-attribute-header" colSpan={5}>
                        {attribute}
                      </td>
                    </tr>
                    <tr key={`attribute-${attribute}-${i}-value`}>
                      {selectedProducts.map((product, n) => {
                        return (
                          <td
                            className="compare-page-attribute-column"
                            key={`attribute-${attribute}-${i}-product-${n}`}
                            width={`${1/selectedProducts.length*100}%`}
                          >
                            <span className={isDiff ? "diff" : ""}>
                              {product.attributes[attribute]}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Container>
    </Container>
  );
};

export default ComparePage;
